"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { TabToggle } from "@/components/common/TabToggle";
import type { ApiWave } from "@/generated/models/ApiWave";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState } from "../ContentTabContext";
import { useWave } from "@/hooks/useWave";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { Time } from "@/helpers/time";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: (tab: MyStreamWaveTab) => void;
}
interface TabOption {
  key: MyStreamWaveTab;
  label: string;
  panelId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const AUTO_EXPAND_LIMIT = 5;
const TAB_LABELS: Record<MyStreamWaveTab, string> = {
  [MyStreamWaveTab.CHAT]: "Chat",
  [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
  [MyStreamWaveTab.WINNERS]: "Winners",
  [MyStreamWaveTab.OUTCOME]: "Outcome",
  [MyStreamWaveTab.MY_VOTES]: "My Votes",
  [MyStreamWaveTab.FAQ]: "FAQ"
};
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(57);
  const {
    activeTab,
    wave,
    setActiveTab
  } = t0;
  const {
    availableTabs,
    updateAvailableTabs,
    setActiveContentTab
  } = useContentTab();
  const {
    isChatWave,
    isMemesWave,
    isCurationWave,
    pauses: t1
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t1;
  const {
    voting: t2,
    decisions: t3
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted,
    isInProgress
  } = t2;
  const {
    firstDecisionDone
  } = t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t4);
  let t5;
  if ($[1] !== allDecisions || $[2] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t5 = allDecisions.filter(decision_0 => filtered.some(f => f.decision_time === decision_0.timestamp));
    $[1] = allDecisions;
    $[2] = filterDecisionsDuringPauses;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[4] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[4] = filteredDecisions;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const nextDecisionTime = t6;
  const [autoExpandFutureAttempts, setAutoExpandFutureAttempts] = useState(0);
  let t7;
  let t8;
  if ($[6] !== autoExpandFutureAttempts || $[7] !== hasMoreFuture || $[8] !== loadMoreFuture || $[9] !== nextDecisionTime) {
    t7 = () => {
      const hasUpcoming = !!nextDecisionTime;
      if (hasUpcoming) {
        if (autoExpandFutureAttempts !== 0) {
          setAutoExpandFutureAttempts(0);
        }
        return;
      }
      if (!hasMoreFuture) {
        if (autoExpandFutureAttempts !== 0) {
          setAutoExpandFutureAttempts(0);
        }
        return;
      }
      if (autoExpandFutureAttempts >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        setAutoExpandFutureAttempts(_temp3);
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t8 = [nextDecisionTime, hasMoreFuture, loadMoreFuture, autoExpandFutureAttempts];
    $[6] = autoExpandFutureAttempts;
    $[7] = hasMoreFuture;
    $[8] = loadMoreFuture;
    $[9] = nextDecisionTime;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== firstDecisionDone || $[13] !== isChatWave || $[14] !== isCompleted || $[15] !== isCurationWave || $[16] !== isMemesWave || $[17] !== isUpcoming || $[18] !== updateAvailableTabs || $[19] !== wave) {
    t9 = () => {
      const votingState = isUpcoming ? WaveVotingState.NOT_STARTED : isCompleted ? WaveVotingState.ENDED : WaveVotingState.ONGOING;
      updateAvailableTabs(wave ? {
        waveId: wave.id,
        isMemesWave,
        isChatWave,
        isCurationWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone
      } : null);
    };
    $[12] = firstDecisionDone;
    $[13] = isChatWave;
    $[14] = isCompleted;
    $[15] = isCurationWave;
    $[16] = isMemesWave;
    $[17] = isUpcoming;
    $[18] = updateAvailableTabs;
    $[19] = wave;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== firstDecisionDone || $[22] !== isChatWave || $[23] !== isCompleted || $[24] !== isCurationWave || $[25] !== isInProgress || $[26] !== isMemesWave || $[27] !== isUpcoming || $[28] !== updateAvailableTabs || $[29] !== wave) {
    t10 = [wave, isMemesWave, isChatWave, isCurationWave, isUpcoming, isCompleted, isInProgress, firstDecisionDone, updateAvailableTabs];
    $[21] = firstDecisionDone;
    $[22] = isChatWave;
    $[23] = isCompleted;
    $[24] = isCurationWave;
    $[25] = isInProgress;
    $[26] = isMemesWave;
    $[27] = isUpcoming;
    $[28] = updateAvailableTabs;
    $[29] = wave;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  useEffect(t9, t10);
  let t11;
  if ($[31] !== setActiveContentTab || $[32] !== wave?.wave?.type) {
    t11 = () => {
      if (wave?.wave?.type === ApiWaveType.Chat) {
        setActiveContentTab(MyStreamWaveTab.CHAT);
      }
    };
    $[31] = setActiveContentTab;
    $[32] = wave?.wave?.type;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const t12 = wave?.wave?.type;
  let t13;
  if ($[34] !== setActiveContentTab || $[35] !== t12) {
    t13 = [t12, setActiveContentTab];
    $[34] = setActiveContentTab;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  useEffect(t11, t13);
  let t14;
  if ($[37] !== availableTabs || $[38] !== isCurationWave || $[39] !== isMemesWave) {
    let t15;
    if ($[41] !== isCurationWave || $[42] !== isMemesWave) {
      t15 = tab => {
        if (tab === MyStreamWaveTab.MY_VOTES) {
          return isMemesWave || isCurationWave;
        }
        if (tab === MyStreamWaveTab.FAQ) {
          return isMemesWave;
        }
        return true;
      };
      $[41] = isCurationWave;
      $[42] = isMemesWave;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    t14 = availableTabs.filter(t15).map(_temp4);
    $[37] = availableTabs;
    $[38] = isCurationWave;
    $[39] = isMemesWave;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const options = t14;
  let t15;
  let t16;
  if ($[44] !== activeTab || $[45] !== isCurationWave || $[46] !== isMemesWave || $[47] !== options || $[48] !== setActiveTab) {
    t15 = () => {
      const isMyVotesHidden = activeTab === MyStreamWaveTab.MY_VOTES && !isMemesWave && !isCurationWave;
      const isFaqHidden = activeTab === MyStreamWaveTab.FAQ && !isMemesWave;
      if ((isMyVotesHidden || isFaqHidden) && options.length > 0) {
        setActiveTab(options[0]?.key);
      }
    };
    t16 = [isMemesWave, isCurationWave, activeTab, options, setActiveTab];
    $[44] = activeTab;
    $[45] = isCurationWave;
    $[46] = isMemesWave;
    $[47] = options;
    $[48] = setActiveTab;
    $[49] = t15;
    $[50] = t16;
  } else {
    t15 = $[49];
    t16 = $[50];
  }
  useEffect(t15, t16);
  if (isChatWave) {
    return null;
  }
  let t17;
  if ($[51] !== setActiveTab) {
    t17 = key => setActiveTab(key as MyStreamWaveTab);
    $[51] = setActiveTab;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== activeTab || $[54] !== options || $[55] !== t17) {
    t18 = <div className="tw-flex tw-w-full tw-items-start tw-justify-between tw-gap-4 tw-overflow-x-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 tw-@container/tabs hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4"><TabToggle options={options} activeKey={activeTab} onSelect={t17} /></div>;
    $[53] = activeTab;
    $[54] = options;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(prev) {
  return prev + 1;
}
function _temp4(tab_0) {
  return {
    key: tab_0,
    label: TAB_LABELS[tab_0],
    panelId: getContentTabPanelId(tab_0)
  };
}